<template>
    <v-popper2
        position="bottomEnd"
    >
        <template #trigger="{popper}">
            <div class="text-left btn btn-fluid" @click="popper.open()">
                <v-payment-instrument :instrument="defaultPaymentInstrument" is-slim>
                    <p slot="noInstrument" class="text-muted no-margin">No default payment instrument.</p>
                </v-payment-instrument>
            </div>
        </template>
        <template #content>
            <div class="popover" style="width: 396px">
                <div class="popover-title" v-if="$auth.editPaymentInstruments.isAllowed">
                    <h2>Manage Payment Instruments</h2>
                    <payment-instrument-form-modal
                        class="no-flex"
                        :customer-id="customer.id"
                        :contacts="contacts"
                        :custom-fields="paymentCardsCustomFields"
                        :is-loading="isLoading"
                        @submit="savePaymentInstrument"
                    />
                </div>
                <div class="popover-control position-relative" :class="{'is-scrollable' : isScrollableInstrumentList}">
                    <v-inline-loader v-if="isLoading"/>
                    <customer-payment-instrument
                        is-default
                        :instrument="defaultPaymentInstrument"
                        @loading="triggerLoading"
                    />
                    <customer-payment-instrument
                        :instrument="paymentInstrument"
                        @loading="triggerLoading"
                        v-for="paymentInstrument in nonDefaultPaymentInstruments"
                        :key="paymentInstrument.id"
                    />
                </div>
            </div>
        </template>
    </v-popper2>
</template>

<script>
    import {mapState, mapActions} from 'vuex';
    import vInlineLoader from '@/components/v-inline-loader';
    import RESOURCES from '@/common/auth/definitions/resources';
    import vPopper2 from '@/components/popper/v-popper2';
    import vPaymentInstrument from '@/components/payment-instrument/v-payment-instrument';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import customerPaymentInstrument from '@/modules/customers/components/customer-payment-instrument';

    const PaymentInstrumentFormModal = () => import('@/modules/customers/components/payment-instrument-manager/form-modal/payment-instrument-form-modal');

    export default {
        components: {
            vPopper2,
            vPaymentInstrument,
            vInlineLoader,
            customerPaymentInstrument,
            PaymentInstrumentFormModal,
        },
        mixins: [LoadStateMixin],
        data() {
            return {
                isLoading: false,
                customFields: null,
                contactsCustomFields: [],
            };
        },
        computed: {
            ...mapState('customers', ['customer', 'contacts', 'paymentInstruments', 'paymentCardsCustomFields']),
            defaultPaymentInstrument() {
                if (this.customer.defaultPaymentInstrument && this.customer.defaultPaymentInstrument.id) {
                    return this.paymentInstruments.find(instrument => instrument.id === this.customer.defaultPaymentInstrument.id);
                }
                return null;
            },
            nonDefaultPaymentInstruments() {
                if (this.defaultPaymentInstrument) {
                    return this.paymentInstruments.filter(instrument => instrument.id !== this.defaultPaymentInstrument.id);
                }
                return this.paymentInstruments;
            },
            isScrollableInstrumentList() {
                return this.paymentInstruments.length > 2;
            },
        },
        provide() {
            return {
                getInstrumentApiPromise: this.getInstrumentApiPromise,
                contactsCustomFields: this.contactsCustomFields,
            };
        },
        methods: {
            ...mapActions({
                fetchPaymentInstruments: 'customers/fetchPaymentInstruments',
                addPaymentInstrument: 'customers/addPaymentInstrument',
            }),
            async fetchData() {
                this.isLoading = true;
                const contactCustomFieldsParam = {
                    resource: RESOURCES.contacts,
                };
                try {
                    await this.fetchPaymentInstruments();
                    this.contactsCustomFields = await this.$api(({customFields}) => customFields.allowedOnly
                        .getAll(contactCustomFieldsParam));
                } catch (error) {
                    this.$toast.fromError(error, 'Could not load payment instrument information, please try again or contact support');
                } finally {
                    this.isLoading = false;
                }
            },
            triggerLoading(value) {
                this.isLoading = value;
            },
            async savePaymentInstrument(instrument) {
                this.isLoading = true;
                const apiPromise = this.getInstrumentApiPromise(instrument, 'create');
                const param = {
                    data: instrument.getPayload(),
                };
                try {
                    const {fields: savedInstrument} = await this.$api(apiPromise(param));
                    this.addPaymentInstrument(savedInstrument);
                    this.$toast.positive('Payment instrument successfully saved');
                } catch (error) {
                    this.$toast.fromError(error, 'Payment instrument could not be saved');
                } finally {
                    this.isLoading = false;
                }
            },
            getInstrumentApiPromise(instrument, action) {
                if (instrument.routingNumber) {
                    return params => ({bankAccounts}) => bankAccounts[action](params);
                }
                if (instrument.pan || instrument.bin) {
                    return params => ({paymentCards}) => paymentCards[action](params);
                }
                if (instrument.username) {
                    return params => ({paypalAccounts}) => paypalAccounts[action](params);
                }
                return null;
            },
        },
    };
</script>
